import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Shield, AlertTriangle, CheckCircle2, AlertCircle } from 'lucide-react';

interface SecurityScoreCardProps {
  score: number;
  level: 'Excellent' | 'Good' | 'Fair' | 'Needs Attention';
}

export const SecurityScoreCard = ({ score, level }: SecurityScoreCardProps) => {
  const getScoreColor = () => {
    if (score >= 85) return 'text-green-600 dark:text-green-400';
    if (score >= 70) return 'text-blue-600 dark:text-blue-400';
    if (score >= 50) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getScoreBgColor = () => {
    if (score >= 85) return 'from-green-500/20 to-green-600/20';
    if (score >= 70) return 'from-blue-500/20 to-blue-600/20';
    if (score >= 50) return 'from-yellow-500/20 to-yellow-600/20';
    return 'from-red-500/20 to-red-600/20';
  };

  const getIcon = () => {
    if (score >= 85) return <Shield className="w-12 h-12 text-green-600 dark:text-green-400" />;
    if (score >= 70) return <CheckCircle2 className="w-12 h-12 text-blue-600 dark:text-blue-400" />;
    if (score >= 50) return <AlertCircle className="w-12 h-12 text-yellow-600 dark:text-yellow-400" />;
    return <AlertTriangle className="w-12 h-12 text-red-600 dark:text-red-400" />;
  };

  const getBadgeVariant = () => {
    if (score >= 85) return 'default';
    if (score >= 70) return 'secondary';
    if (score >= 50) return 'outline';
    return 'destructive';
  };

  // Calculate circle progress
  const circumference = 2 * Math.PI * 80;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  return (
    <Card className="overflow-hidden">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="w-5 h-5" />
          Security Score
        </CardTitle>
        <CardDescription>
          Your overall account security rating
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-center space-y-6">
          {/* Circular progress */}
          <div className="relative">
            <svg className="transform -rotate-90" width="200" height="200">
              {/* Background circle */}
              <circle
                cx="100"
                cy="100"
                r="80"
                stroke="currentColor"
                strokeWidth="12"
                fill="none"
                className="text-muted/20"
              />
              {/* Progress circle */}
              <circle
                cx="100"
                cy="100"
                r="80"
                stroke="currentColor"
                strokeWidth="12"
                fill="none"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                strokeLinecap="round"
                className={`${getScoreColor()} transition-all duration-1000 ease-out`}
              />
            </svg>
            {/* Center content */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              {getIcon()}
              <span className={`text-4xl font-bold mt-2 ${getScoreColor()}`}>
                {score}
              </span>
              <span className="text-sm text-muted-foreground">out of 100</span>
            </div>
          </div>

          {/* Level badge */}
          <Badge variant={getBadgeVariant()} className="text-base px-4 py-1">
            {level}
          </Badge>

          {/* Recommendations */}
          <div className={`w-full p-4 rounded-lg bg-gradient-to-br ${getScoreBgColor()}`}>
            <h4 className="font-semibold mb-2">Security Tips:</h4>
            <ul className="text-sm space-y-1 text-muted-foreground">
              {score < 85 && (
                <>
                  {score < 70 && (
                    <li>• Enable two-factor authentication for extra protection</li>
                  )}
                  {score < 50 && (
                    <li>• Review and revoke suspicious login sessions</li>
                  )}
                  <li>• Use a strong, unique password</li>
                  <li>• Regularly review your login history</li>
                </>
              )}
              {score >= 85 && (
                <li>• Great work! Keep monitoring your security dashboard regularly</li>
              )}
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
